import styled from "styled-components";
import {
  border,
  brand,
  radius,
  spacing,
  status,
  surface,
  text,
  typography,
} from "../../../theme/tokens";

/* ------------------------------------------------------------------ *
 * Top bar: quiet toolbar verbs on the left of the person search,
 * which sits top right (approved mock, 8/31).
 * ------------------------------------------------------------------ */

export const TopBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: ${spacing[2]};
  padding: ${spacing[0.5]} 0 ${spacing[3]};
  flex-wrap: wrap;
`;

export const SearchWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1.5]};
  width: 300px;
  max-width: 100%;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: ${spacing[1.5]} ${spacing[3]};
  font-size: ${typography.size.base};
  font-family: inherit;
  color: ${text.primary};
  background: ${surface.surface};
  border: 1px solid ${border.default};
  border-radius: ${radius.pill};
  outline: none;

  &::placeholder {
    color: ${text.muted};
  }

  &:focus {
    border-color: ${border.focus};
  }
`;

export const SuggestionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[1.5]};
`;

export const SuggestionPill = styled.button`
  padding: ${spacing[0.5]} ${spacing[2.5]};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  font-family: inherit;
  color: ${text.secondary};
  background: ${surface.surface};
  border: 1px solid ${border.default};
  border-radius: ${radius.pill};
  cursor: pointer;

  &:hover {
    color: ${brand.primary};
    border-color: ${brand.primary};
  }
`;

/** "You're seeing {name}'s plate…" — info tone, not a warning. */
export const ViewingBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  flex-wrap: wrap;
  margin: 0 0 ${spacing[3]};
  padding: ${spacing[2]} ${spacing[3]};
  border-radius: ${radius.lg};
  border: 1px solid ${status.processing.border};
  background: ${status.processing.bg};
  color: ${status.processing.fg};
  font-size: ${typography.size.base};
`;

export const ViewingBannerSpacer = styled.span`
  flex: 1;
`;

export const QuietNote = styled.p`
  margin: 0 0 ${spacing[2]};
  font-size: ${typography.size.sm};
  color: ${text.muted};
  text-align: right;
`;

/* ------------------------------------------------------------------ *
 * Sections
 * ------------------------------------------------------------------ */

export const SectionHeaderRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${spacing[2.5]};
  flex-wrap: wrap;
  margin: ${spacing[2]} 0 ${spacing[2]};
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.bold};
  color: ${text.primary};
  letter-spacing: -0.01em;
`;

export const SectionSpacer = styled.span`
  flex: 1;
`;

/* ------------------------------------------------------------------ *
 * "On a clock" — the deadline table. Urgency colors the Due cell:
 * red = overdue / ≤2d, amber = ≤7d.
 * ------------------------------------------------------------------ */

export const ClockTableWrap = styled.div`
  background: ${surface.surface};
  border: 1px solid ${border.light};
  border-radius: ${radius.xl};
  overflow: hidden;
  margin-bottom: ${spacing[5]};
`;

export const ClockTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${typography.size.base};

  th {
    text-align: left;
    padding: ${spacing[2]} ${spacing[3]};
    font-size: ${typography.size.xs};
    font-weight: ${typography.weight.bold};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${text.tertiary};
    border-bottom: 1px solid ${border.light};
    white-space: nowrap;
  }

  td {
    padding: ${spacing[2]} ${spacing[3]};
    border-bottom: 1px solid ${border.light};
    color: ${text.secondary};
    vertical-align: top;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const urgencyColor = ({ $urgency }) =>
  $urgency === "over" || $urgency === "hot"
    ? status.failed.fg
    : $urgency === "soon"
      ? status.review.fg
      : text.primary;

export const ClockDueCell = styled.td`
  && {
    font-weight: ${typography.weight.semibold};
    white-space: nowrap;
    color: ${urgencyColor};
  }

  small {
    font-weight: ${typography.weight.regular};
    font-size: ${typography.size.sm};
  }
`;

/* The expanded work item lives in a full-width row under its own line,
 * so a Due Soon item opens exactly like one inside a window. */
export const ClockExpandCell = styled.td`
  && {
    padding-top: 0;
    background: ${surface.surface};
  }
`;

export const ClockTitleButton = styled.button`
  display: block;
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: ${typography.size.base};
  font-weight: ${typography.weight.medium};
  font-family: inherit;
  color: ${text.primary};

  &:hover {
    color: ${brand.primary};
  }
`;

/* ------------------------------------------------------------------ *
 * Grouping toggle pills
 * ------------------------------------------------------------------ */

export const TogglePill = styled.button`
  padding: ${spacing[1]} ${spacing[3]};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  font-family: inherit;
  border-radius: ${radius.pill};
  cursor: pointer;
  color: ${({ $active }) => ($active ? text.onAccent : text.secondary)};
  background: ${({ $active }) => ($active ? brand.primary : surface.surface)};
  border: 1px solid
    ${({ $active }) => ($active ? brand.primary : border.default)};

  &:hover {
    ${({ $active }) => ($active ? "" : `border-color: ${brand.primary};`)}
  }
`;

/* ------------------------------------------------------------------ *
 * Cards — "windows, a bit like Salesforce" (Matthew, 8/31 redlines).
 * Laid out by PackedCards (columns, shortest-first) since 9/10; the
 * grid it replaced left short cards stranded under tall neighbours.
 * ------------------------------------------------------------------ */

export const MatterCard = styled.section`
  background: ${surface.surface};
  border: 1px solid ${border.light};
  border-radius: ${radius.xl};
  overflow: hidden;
`;

export const MatterCardHeader = styled.h3`
  margin: 0;
  padding: ${spacing[2.5]} ${spacing[3.5]};
  font-size: ${typography.size.base};
  font-weight: ${typography.weight.bold};
  color: ${text.primary};
  border-bottom: 1px solid ${border.light};
  background: ${surface.folderTint};
`;

/* ------------------------------------------------------------------ *
 * Rows — kind tag · title · from · due chip · age.
 * Color = meaning: the due chip and age text go red/amber only when
 * the dates say so.
 * ------------------------------------------------------------------ */

export const RowShell = styled.div`
  padding: ${spacing[2]} ${spacing[3.5]};
  border-bottom: 1px solid ${border.light};
  ${({ $muted }) => $muted && "opacity: 0.55;"}

  &:last-child {
    border-bottom: none;
  }
`;

/** The one-line state a folded/greyed row shows instead of its usual
 * due/age meta — "Snoozed until 9/20 — asked client for statement",
 * "Coming up — after Order Exam is approved", the doorway's own
 * outsideLabel (Matthew, 9/4). */
export const StateLine = styled.div`
  margin-top: 1px;
  font-size: ${typography.size.sm};
  color: ${text.muted};
`;

/* ------------------------------------------------------------------ *
 * Fold blocks — "Waiting on client / vendor" / "Snoozed". Collapsed
 * by default, no counts ever (Matthew: "no numbers").
 * ------------------------------------------------------------------ */

export const FoldToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  width: 100%;
  text-align: left;
  padding: ${spacing[1.5]} ${spacing[3.5]};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-family: inherit;
  color: ${text.muted};
  background: ${surface.folderTint};
  border: none;
  border-top: 1px solid ${border.light};
  cursor: pointer;

  &:hover {
    color: ${brand.primary};
  }
`;

export const RowTop = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2.5]};
`;

export const KindTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  width: 96px;
  flex-shrink: 0;
  font-size: ${typography.size.xs};
  color: ${text.secondary};
  white-space: nowrap;
`;

export const KindDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: ${radius.pill};
  flex-shrink: 0;
  background: ${({ $color }) => $color || text.muted};
`;

export const RowBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

export const RowTitleButton = styled.button`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: ${typography.size.base};
  font-weight: ${typography.weight.semibold};
  font-family: inherit;
  color: ${text.primary};
  line-height: 1.4;

  &:hover {
    color: ${brand.primary};
  }
`;

export const RowMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing[1]} ${spacing[2]};
  font-size: ${typography.size.sm};
  color: ${text.muted};
`;

export const DueChip = styled.span`
  display: inline-flex;
  align-items: baseline;
  padding: 1px ${spacing[2]};
  border-radius: ${radius.pill};
  background: ${surface.hover};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;
  flex-shrink: 0;
  color: ${({ $urgency }) =>
    $urgency === "over" || $urgency === "hot"
      ? status.failed.fg
      : $urgency === "soon"
        ? status.review.fg
        : text.secondary};
`;

export const AgeText = styled.span`
  font-size: ${typography.size.xs};
  white-space: nowrap;
  flex-shrink: 0;
  color: ${({ $tone }) =>
    $tone === "red"
      ? status.failed.fg
      : $tone === "amber"
        ? status.review.fg
        : text.disabled};
`;

/** A progress flag on a collapsed work-item row (Matthew, 9/9): the
 * last logged step (quiet), the follow-up date ("flag" amber, "over"
 * red once past), "No document attached" (red). Same tones as the
 * chip strip inside the expanded card. */
export const RowFlag = styled.span`
  display: inline-block;
  padding: 0 ${spacing[1.5]};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.medium};
  line-height: 16px;
  white-space: nowrap;
  border-radius: ${radius.sm};
  background: ${({ $tone }) =>
    $tone === "over"
      ? status.failed.bg
      : $tone === "flag"
        ? status.review.bg
        : status.neutral.bg};
  color: ${({ $tone }) =>
    $tone === "over"
      ? status.failed.fg
      : $tone === "flag"
        ? status.review.fg
        : text.secondary};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "over"
        ? status.failed.border
        : $tone === "flag"
          ? status.review.border
          : border.light};
`;

export const RowVerbButton = styled.button`
  padding: ${spacing[1]} ${spacing[2.5]};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  font-family: inherit;
  color: ${text.secondary};
  background: ${surface.surface};
  border: 1px solid ${border.default};
  border-radius: ${radius.sm};
  cursor: pointer;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    color: ${brand.primary};
    border-color: ${brand.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/** Expanded work item: the full WorkItemCard (instruction, thread,
 * doorway verbs) slides in under the row. */
export const RowExpand = styled.div`
  margin-top: ${spacing[2]};
`;

export const PlateEmpty = styled.div`
  padding: ${spacing[6]} ${spacing[2]} ${spacing[8]};
  text-align: center;
  font-size: ${typography.size.base};
  color: ${text.muted};
  background: ${surface.surface};
  border: 1px solid ${border.light};
  border-radius: ${radius.xl};
`;

/* ------------------------------------------------------------------ *
 * Create Work Item — the matter-picker step.
 * ------------------------------------------------------------------ */

export const PickerHint = styled.p`
  margin: 0 0 ${spacing[2]};
  font-size: ${typography.size.base};
  color: ${text.secondary};
`;

export const PickerStatus = styled.p`
  margin: ${spacing[2.5]} 0 0;
  font-size: ${typography.size.sm};
  color: ${text.muted};
`;

export const MatterOption = styled.button`
  display: block;
  width: 100%;
  margin-top: ${spacing[1.5]};
  padding: ${spacing[2]} ${spacing[3]};
  text-align: left;
  font-size: ${typography.size.base};
  font-weight: ${typography.weight.semibold};
  font-family: inherit;
  color: ${text.primary};
  background: ${surface.surface};
  border: 1px solid ${border.default};
  border-radius: ${radius.lg};
  cursor: pointer;

  &:hover {
    border-color: ${brand.primary};
    color: ${brand.primary};
  }
`;

export const DegradedBanner = styled.div`
  margin: 0 0 ${spacing[3]};
  padding: ${spacing[2]} ${spacing[3]};
  border-radius: ${radius.lg};
  border: 1px solid ${status.review.border};
  background: ${status.review.bg};
  color: ${status.review.fg};
  font-size: ${typography.size.sm};
`;

export const LoadingNote = styled.div`
  padding: ${spacing[6]} ${spacing[2]} ${spacing[8]};
  text-align: center;
  font-size: ${typography.size.base};
  color: ${text.muted};
`;
